'use client'

import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import { CheckedState } from '@radix-ui/react-checkbox'
import { ComponentProps, ReactNode } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import { cn } from '@/shared/lib'
import { Icon } from '@/shared/ui'

type Props = Omit<
  ComponentProps<typeof CheckboxPrimitive.Root>,
  'size' | 'name' | 'children'
> & {
  name: string
  size: 'small' | 'medium' | 'large'
  children: ReactNode | ((isChecked: boolean) => ReactNode)
}

export const OptionBox = ({
  name,
  children,
  size,
  onCheckedChange,
  className,
  ...restProps
}: Props) => {
  const { control, setValue } = useFormContext()

  const isChecked = useWatch({ control, name }) ?? false

  const handleCheckedChange = (checked: CheckedState) => {
    setValue(name, !isChecked)
    onCheckedChange?.(checked)
  }

  return (
    <CheckboxPrimitive.Root
      checked={isChecked}
      onCheckedChange={handleCheckedChange}
      className={cn(
        'flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg bg-gray-50 px-5',
        'transition-all duration-200',
        'outline-none focus-visible:ring-2 focus-visible:ring-gray-500/50',
        {
          'rounded-sm py-4 pr-3 pl-4': size === 'small',
          'py-2': size === 'medium',
          'py-5': size === 'large',
          'bg-purple-50 focus-visible:ring-purple-500/50': isChecked,
          'shadow-[inset_0_0_0_1px_black]': isChecked && size !== 'medium',
        },
        className,
      )}
      {...restProps}
    >
      <div
        className={cn(
          'flex grow items-center gap-3 text-left text-black select-none',
          {
            'typo-label1 font-medium': size === 'small',
            'typo-body1 font-semibold': size === 'medium',
            'typo-body1-reading font-medium': size === 'large',
          },
        )}
      >
        {typeof children === 'function' ? children(isChecked) : children}
      </div>

      <div
        className={cn(
          'flex h-6 w-6 items-center justify-center rounded-full bg-gray-200',
          'transition-all duration-200',
          {
            'h-4.5 w-4.5 min-w-4.5': size === 'small',
            'bg-black': isChecked,
          },
        )}
      >
        <Icon
          name="check"
          size={size === 'small' ? 12 : 16}
          strokeWidth={size === 'small' ? 1 : 1.5}
          className="text-white"
        />
      </div>
    </CheckboxPrimitive.Root>
  )
}
